/**
 * Enum values for reservation special requests, occasions, and dietary restrictions.
 * Form values remain snake_case names and are converted to tag UUIDs before API submit.
 */

export interface SelectOption {
  value: string;
  labelKey: string;
}

export const OCCASIONS_ENUM = [
  'business_meeting',
  'corporate_event',
  'birthday',
  'name_day',
  'honeymoon',
  'graduation',
  'engagement',
  'anniversary',
  'baby_shower',
  'bachelor',
  'wedding',
] as const;

export const OCCASIONS_OPTIONS: SelectOption[] = [
  { value: 'business_meeting', labelKey: 'occasions.business_meeting' },
  { value: 'corporate_event', labelKey: 'occasions.corporate_event' },
  { value: 'birthday', labelKey: 'occasions.birthday' },
  { value: 'name_day', labelKey: 'occasions.name_day' },
  { value: 'honeymoon', labelKey: 'occasions.honeymoon' },
  { value: 'graduation', labelKey: 'occasions.graduation' },
  { value: 'engagement', labelKey: 'occasions.engagement' },
  { value: 'anniversary', labelKey: 'occasions.anniversary' },
  { value: 'baby_shower', labelKey: 'occasions.baby_shower' },
  { value: 'bachelor', labelKey: 'occasions.bachelor' },
  { value: 'wedding', labelKey: 'occasions.wedding' },
];

// Dietary Restrictions
// Note: API has typo "glutten_intolerant" instead of "gluten_intolerant"
export const DIETARY_ENUM = [
  'glutten_intolerant',
  'allium',
  'eggs',
  'sea_food',
  'wheat',
  'milk',
  'nuts',
  'vegetarian',
  'lactose_intolerant',
  'pescetarian',
  'diabetic',
  'mushrooms',
  'sesame',
  'halal',
  'sulfites',
  'soy',
] as const;

export const DIETARY_OPTIONS: SelectOption[] = [
  { value: 'glutten_intolerant', labelKey: 'dietary.glutten_intolerant' },
  { value: 'allium', labelKey: 'dietary.allium' },
  { value: 'eggs', labelKey: 'dietary.eggs' },
  { value: 'sea_food', labelKey: 'dietary.sea_food' },
  { value: 'wheat', labelKey: 'dietary.wheat' },
  { value: 'milk', labelKey: 'dietary.milk' },
  { value: 'nuts', labelKey: 'dietary.nuts' },
  { value: 'vegetarian', labelKey: 'dietary.vegetarian' },
  { value: 'lactose_intolerant', labelKey: 'dietary.lactose_intolerant' },
  { value: 'pescetarian', labelKey: 'dietary.pescetarian' },
  { value: 'diabetic', labelKey: 'dietary.diabetic' },
  { value: 'mushrooms', labelKey: 'dietary.mushrooms' },
  { value: 'sesame', labelKey: 'dietary.sesame' },
  { value: 'halal', labelKey: 'dietary.halal' },
  { value: 'sulfites', labelKey: 'dietary.sulfites' },
  { value: 'soy', labelKey: 'dietary.soy' },
];

export type Occasion = typeof OCCASIONS_ENUM[number];
export type Dietary = typeof DIETARY_ENUM[number];
export type ReservationTagName = Occasion | Dietary;

export const RESERVATION_TAG_UUID_BY_NAME: Record<ReservationTagName, string> = {
  business_meeting: 'f4fe1bc1-726f-4ba4-97b3-87dad121a675',
  corporate_event: '928c903f-0102-48ba-a7e9-121e3d5c56c8',
  birthday: 'ac7ddbda-8a7d-4ef9-a022-fbcab662e22c',
  name_day: '93ade3c8-d62c-4abe-822b-59520418a38e',
  honeymoon: '617fe4a3-0d71-4dc3-939b-cd42c7fb02e9',
  graduation: '8e298060-c296-435c-8507-e3b674df32d0',
  engagement: 'd57c477c-645e-469e-9910-461c6655747a',
  anniversary: '7b58301b-e745-4a81-9b91-fae1a17a4b1e',
  baby_shower: '9ffee640-2fbd-4f4b-8565-c193ca2e57ed',
  bachelor: 'f0db91d5-5c38-4497-90e9-b034299d9eab',
  wedding: '072156cb-4975-4454-b898-7180d0420dee',
  glutten_intolerant: 'ff1802c6-64ff-4a6a-bdaa-1bf7c9576883',
  allium: '67bd528e-f805-4017-8691-845d89034182',
  eggs: 'c1a55bb0-b778-42eb-8910-71de2d86cae1',
  sea_food: 'c0d3616d-3d46-4496-9622-b6fb0e03ff4c',
  wheat: 'af102a63-7388-428d-a019-ed4c5fb7654e',
  milk: 'e7026db3-519c-42d6-a9fa-96cf313a7d88',
  nuts: 'b58d621d-5e3b-40d9-91be-7378b90fdec1',
  vegetarian: 'e3a2f245-ccd4-4b79-b622-880695189847',
  lactose_intolerant: '254234b7-c5f3-4727-a413-827aff13f540',
  pescetarian: 'fdeba9ae-f12d-4700-9f0b-983c8a222c4a',
  diabetic: 'd96f5b5d-d28b-4114-a4c7-9e45544cd5cb',
  mushrooms: 'e2ee9ab4-fa86-49da-b373-92a0ec83524f',
  sesame: 'b8b18990-dcc5-4e41-8a64-9d2c9a9c9d7d',
  halal: '8616b248-ea1f-41d6-aa4d-366875b7fd13',
  sulfites: '7ef40940-60c7-450a-99fa-022764911e5d',
  soy: '1c49946c-aadf-41b3-8089-cdbab6e0a82b',
};

export const mapReservationTagNamesToUuids = (tagNames: ReservationTagName[]): string[] =>
  [...new Set(tagNames.map((tagName) => RESERVATION_TAG_UUID_BY_NAME[tagName]))];

export const isReservationTagName = (value: string): value is ReservationTagName =>
  value in RESERVATION_TAG_UUID_BY_NAME;
